import { useEffect, useCallback } from 'react';
import { trackEmailLinkClick } from '../lib/email-link-tracker';
import { setCookieValue, getCookieValue } from '../lib/cookies';
import { AdvocateProfile } from '../models/advocate/types';
import { ProgramMembership } from '../models/program-membership/types';
import { Program } from '../models/program/types';

// Analytics and Pendo interfaces
interface Analytics {
  ready: (callback: () => void) => void;
  identify: (id: string, traits: Record<string, unknown>) => void;
}

interface PendoTool {
  initialize: (options: {
    visitor: Record<string, unknown>;
    account: Record<string, unknown>;
  }) => void;
}

/**
 * Type-safe access to window.analytics
 */
const getAnalytics = (): Analytics | undefined => {
  return (window as any).analytics as Analytics | undefined;
};

/**
 * Type-safe access to window.pendo
 */
const getPendo = (): PendoTool | undefined => {
  return (window as any).pendo as PendoTool | undefined;
};

/**
 * Initialize analytics IDs in localStorage and cookies
 * @param programMembershipId - Program membership ID to use for analytics
 */
export const initAnalyticsIds = (programMembershipId?: string): void => {
  if (!programMembershipId) return;

  const ANALYTICS_USER_ID_KEY = 'ajs_user_id';
  const analyticsUserId = `"${programMembershipId}"`;

  if (localStorage.getItem(ANALYTICS_USER_ID_KEY) !== analyticsUserId) {
    localStorage.setItem(ANALYTICS_USER_ID_KEY, analyticsUserId);
  }

  if (getCookieValue(ANALYTICS_USER_ID_KEY) !== analyticsUserId) {
    setCookieValue(ANALYTICS_USER_ID_KEY, analyticsUserId, true, {
      domain: `.${window.location.hostname}`,
      sameSite: 'lax',
    });
  }
};

/**
 * Identify the user in analytics
 * @param membership - Program membership data
 * @param program - Program data
 * @param advocate - Advocate data
 */
export const identifyAnalyticsUser = (
  membership?: ProgramMembership,
  program?: Program,
  advocate?: AdvocateProfile
): void => {
  const identifier = membership?.id;

  if (!identifier || !program || !advocate) return;

  const analytics = getAnalytics();
  if (analytics) {
    analytics.ready(() => {
      analytics.identify(identifier, {
        program_id: program.id,
        program_title: program.name,
        brand_id: program.brand_id,
        brand_name: program.brand_name,
        advocate_id: advocate.id,
      });

      trackEmailLinkClick(analytics, {
        user_id: identifier,
        program_id: program.id,
        brand_id: program.brand_id,
      });
    });
  }
};

/**
 * Initialize analytics tracking
 * @param segaServiceUrl - URL for analytics service
 * @param callback - Optional callback to run when analytics are ready
 */
export const initAnalytics = (
  segaServiceUrl: string,
  callback?: () => void
): void => {
  const title = document.head.querySelector('title');
  if (!title || !title.parentNode) return;

  const sega = document.createElement('script');
  sega.async = true;
  sega.src = segaServiceUrl;
  title.parentNode.insertBefore(sega, title.nextSibling);

  if (callback) {
    sega.onload = callback;
  }
};

/**
 * Initialize Pendo tracking
 * @param membership - Program membership data
 * @param program - Program data
 * @param advocate - Advocate data
 */
export const initPendo = (
  membership?: ProgramMembership,
  program?: Program,
  advocate?: AdvocateProfile
): void => {
  if (
    !membership?.id ||
    !program?.id ||
    !advocate?.email ||
    membership.status !== 'active'
  ) {
    return;
  }

  const pendo = getPendo();
  if (!pendo) return;

  pendo.initialize({
    visitor: {
      id: membership.id,
      email: advocate.email,
      status: membership.status,
    },
    account: {
      id: program.id,
      name: program.name,
    },
  });
};

/**
 * Custom hook to set up analytics
 * @param segaServiceUrl - URL for analytics service
 * @param membership - Program membership data
 * @param program - Program data
 * @param advocate - Advocate data
 * @param isInitialized - Whether the app state is initialized
 */
export const useAnalytics = (
  segaServiceUrl?: string,
  membership?: ProgramMembership,
  program?: Program,
  advocate?: AdvocateProfile,
  isInitialized?: boolean
): void => {
  // Initialize analytics IDs
  const setupAnalyticsIds = useCallback(() => {
    if (membership?.id) {
      initAnalyticsIds(membership.id);
    }
  }, [membership?.id]);

  // Setup analytics identification
  const identifyUser = useCallback(() => {
    identifyAnalyticsUser(membership, program, advocate);
  }, [membership, program, advocate]);

  // Initialize analytics script
  const setupAnalytics = useCallback(() => {
    if (segaServiceUrl) {
      initAnalytics(segaServiceUrl, identifyUser);
    }
  }, [segaServiceUrl, identifyUser]);

  // Initialize Pendo
  const setupPendo = useCallback(() => {
    initPendo(membership, program, advocate);
  }, [membership, program, advocate]);

  // Run all analytics initialization when the app is ready
  useEffect(() => {
    if (isInitialized) {
      setupAnalyticsIds();
      setupAnalytics();
      setupPendo();
    }
  }, [isInitialized, setupAnalyticsIds, setupAnalytics, setupPendo]);
};
